import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CrudListToolbar from "../entity/CrudListToolbar";
import ListPaginationBar from "../ListPaginationBar";
import DataRefreshControl from "../DataRefreshControl";
import { getTableFreezeColsClassName, TableActionColumnHeader, TableViewport } from "../TableShell";
import { useEntityListChrome } from "../../shared/hooks/useEntityListChrome";
import { useListPagination } from "../../shared/hooks/useListPagination";
import { downloadCsvTextFromString } from "../../shared/utils/csvFileHelpers";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { buildStockArkEtfCsvExportText } from "../../shared/utils/stockArkEtfCsv";
import { formatBatchSummaryMessage } from "../../shared/utils/batchSummaryMessage";
import { bulkSequentialDelete } from "../../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../../shared/utils/entityListSelection";
import { sortRowsByConfig } from "../../shared/utils/sortRowsByConfig";
import { stockRequestBody } from "../../shared/api/stockContext";
import { getArkBuyTradesSource, listStockArkBuy } from "../../shared/api/stockArkBuy";
import StockSummaryStatCards from "./StockSummaryStatCards";
import StockHoldingsTableCell from "./StockHoldingsTableCell";
import {
  countStocksWithUpcomingEarnings,
  filterStocksWithUpcomingEarnings,
} from "../../shared/utils/stockEarningsUpcoming";
import {
  countStocksWithRecentBuy,
  filterStocksWithRecentBuy,
} from "../../shared/utils/stockArkRecentBuy";
import {
  countStocksWithPositiveEquity,
  filterStocksWithPositiveEquity,
} from "../../shared/utils/stockCurrentHolding";
import { summarizeStockListRows } from "../../shared/utils/stockSummary";
import { iconBtnStyle } from "../../shared/icons/IconLib";
import {
  STOCK_TABLE_SORT_KEY_TYPES,
  resolveArkBuyVisibleColumns,
  stockHoldingsColumnsFromIds,
  stockHoldingsRowMatchesSearch,
} from "../../shared/utils/stockHoldingsTable";
import { apiFetch } from "../../shared/api/apiFetch";

function arkBuyRowMatchesSearch(row, searchTerm) {
  if (stockHoldingsRowMatchesSearch(row, searchTerm)) return true;
  const searchLower = (searchTerm || "").toLowerCase().trim();
  if (!searchLower) return true;
  const extra = [row.trade_date, row.ark_market_value, row.ark_weight, row.ark_fund]
    .map((v) => (v != null ? String(v).toLowerCase() : ""))
    .join(" ");
  return extra.includes(searchLower);
}

function sortArkEtfRows(rows, sortConfig) {
  if (!sortConfig?.key) return rows;
  const key =
    sortConfig.key === "ark_market_value"
      ? "ark_market_value_raw"
      : sortConfig.key === "ark_weight"
        ? "ark_weight_raw"
        : sortConfig.key;
  return sortRowsByConfig(rows, { ...sortConfig, key }, STOCK_TABLE_SORT_KEY_TYPES);
}

function StockArkBuy({
  stockSession,
  currentUserId,
  serverUrl,
  onError,
  setConfirmDialog,
  earningsUpcomingFilter = false,
  onUpcomingEarningsClick,
  recentBuyFilter = false,
  onRecentBuyClick,
  currentHoldingFilter = false,
  onCurrentHoldingClick,
  onOpenStock,
  onCountChanged,
  visibleColumnIds,
  onOpenColumnCustomizer,
  columnPrefsSaving = false,
}) {
  const accountId = stockSession?.account_id;
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedList, setHasLoadedList] = useState(false);
  const [fetchedAt, setFetchedAt] = useState("");
  const [tradesUrl, setTradesUrl] = useState("");
  const [holdingsUrl, setHoldingsUrl] = useState("");
  const [exporting, setExporting] = useState(false);

  const displayColumnIds = useMemo(
    () => resolveArkBuyVisibleColumns(visibleColumnIds),
    [visibleColumnIds]
  );

  const displayColumns = useMemo(
    () => stockHoldingsColumnsFromIds(displayColumnIds),
    [displayColumnIds]
  );

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: "symbol", direction: "asc" } });

  useEffect(() => {
    if (!recentBuyFilter) return;
    setSortConfig({ key: "trade_date", direction: "desc" });
  }, [recentBuyFilter, setSortConfig]);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCountChangedRef = useRef(onCountChanged);
  onCountChangedRef.current = onCountChanged;
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await getArkBuyTradesSource(serverUrl, stockSession);
        if (!cancelled) {
          setTradesUrl(info?.tradesUrl || info?.url || "");
          setHoldingsUrl(info?.holdingsUrl || "");
          if (info?.lastRefreshAt) setFetchedAt(info.lastRefreshAt);
        }
      } catch (_) {
        if (!cancelled) {
          setTradesUrl("");
          setHoldingsUrl("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, serverUrl, stockSession]);

  const fetchList = useCallback(async () => {
    if (!accountId) return;
    setLoadingList(true);
    try {
      const data = await listStockArkBuy(serverUrl, stockSession);
      setRows(data.rows);
      setFetchedAt(data.fetchedAt || "");
      if (data.tradesUrl) setTradesUrl(data.tradesUrl);
      if (data.holdingsUrl) setHoldingsUrl(data.holdingsUrl);
      setHasLoadedList(true);
      onCountChangedRef.current?.(data.totalMatched);
    } catch (err) {
      onErrorRef.current(getMessageForFetchError(err));
      setRows([]);
      setHasLoadedList(true);
      onCountChangedRef.current?.(0);
    } finally {
      setLoadingList(false);
    }
  }, [accountId, serverUrl, stockSession]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const pollArkRefreshJob = useCallback(
    async (jobId) => {
      const pollBody = stockRequestBody(stockSession, { jobId });
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const pollResp = await apiFetch(`${serverUrl}/getStockArkBuyRefreshStatus`, {
          method: "POST",
          body: JSON.stringify(pollBody),
        });
        const pollData = await pollResp.json().catch(() => ({}));
        if (pollData.status === "pending") continue;
        if (!pollResp.ok || pollData.status === "error") {
          const detail =
            pollData.message ||
            pollData.error ||
            (pollData.result && (pollData.result.message || pollData.result.error)) ||
            (pollResp.status ? `Server returned HTTP ${pollResp.status}` : "");
          throw new Error(detail || "ARK ETF refresh failed");
        }
        return pollData;
      }
      throw new Error("ARK ETF refresh is still running on the server. Wait a minute, then reload this tab.");
    },
    [serverUrl, stockSession]
  );

  const refreshFromArk = useCallback(async () => {
    if (!accountId) return;
    if (!currentUserId) {
      onError("User session is required to refresh ARK ETF.");
      return;
    }
    setRefreshing(true);
    onError("");
    try {
      const resp = await apiFetch(`${serverUrl}/refreshStockArkBuy`, {
        method: "POST",
        body: JSON.stringify(stockRequestBody(stockSession, { created_by: currentUserId })),
      });
      let data = await resp.json().catch(() => ({}));
      if (resp.status === 202 && data.async && data.jobId) {
        data = await pollArkRefreshJob(data.jobId);
      } else if (!resp.ok) {
        throw new Error(data.message || data.error || "Failed to refresh ARK ETF list");
      }
      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list);
      setFetchedAt(data.fetchedAt || "");
      if (data.tradesUrl) setTradesUrl(data.tradesUrl);
      if (data.holdingsUrl) setHoldingsUrl(data.holdingsUrl);
      setHasLoadedList(true);
      setSelectedIds(new Set());
      onCountChangedRef.current?.(
        data.totalMatched != null ? data.totalMatched : list.length
      );
    } catch (err) {
      onError(getMessageForFetchError(err));
    } finally {
      setRefreshing(false);
    }
  }, [
    accountId,
    currentUserId,
    serverUrl,
    stockSession,
    onError,
    pollArkRefreshJob,
    setSelectedIds,
  ]);

  const searchFilteredRows = useMemo(
    () => rows.filter((row) => arkBuyRowMatchesSearch(row, searchTerm)),
    [rows, searchTerm]
  );

  const upcomingEarningsCount = useMemo(
    () => countStocksWithUpcomingEarnings(searchFilteredRows),
    [searchFilteredRows]
  );

  const recentBuyCount = useMemo(
    () => countStocksWithRecentBuy(searchFilteredRows),
    [searchFilteredRows]
  );

  const currentHoldingCount = useMemo(
    () => countStocksWithPositiveEquity(searchFilteredRows),
    [searchFilteredRows]
  );

  const recentBuyFilteredRows = useMemo(() => {
    if (!recentBuyFilter) return searchFilteredRows;
    return filterStocksWithRecentBuy(searchFilteredRows);
  }, [searchFilteredRows, recentBuyFilter]);

  const currentHoldingFilteredRows = useMemo(() => {
    if (!currentHoldingFilter) return recentBuyFilteredRows;
    return filterStocksWithPositiveEquity(recentBuyFilteredRows);
  }, [recentBuyFilteredRows, currentHoldingFilter]);

  const earningsFilteredRows = useMemo(() => {
    if (!earningsUpcomingFilter) return currentHoldingFilteredRows;
    return filterStocksWithUpcomingEarnings(currentHoldingFilteredRows);
  }, [currentHoldingFilteredRows, earningsUpcomingFilter]);

  const sortedRows = useMemo(
    () => sortArkEtfRows(earningsFilteredRows, sortConfig),
    [earningsFilteredRows, sortConfig]
  );

  const listSummary = useMemo(() => summarizeStockListRows(sortedRows), [sortedRows]);

  const {
    pageItems,
    paginationBarProps,
    getSelectAllCheckboxProps,
  } = useListPagination(
    sortedRows,
    [
      searchTerm,
      sortConfig?.key,
      sortConfig?.direction,
      recentBuyFilter,
      currentHoldingFilter,
      earningsUpcomingFilter,
      displayColumnIds.join(","),
    ],
    { initialPageSize: 10 }
  );

  const busy = loadingList || refreshing;
  const showTable = hasLoadedList && sortedRows.length > 0;
  const showEmptyState = hasLoadedList && !loadingList && sortedRows.length === 0;
  const freezeClass = getTableFreezeColsClassName(2, 1);

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, sortedRows);
  }

  function exportSelectedCsv() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one stock to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const idSet = selectedVisibleIds;
      const selected = sortedRows
        .filter((row) => row && idSet.has(row._id))
        .sort((a, b) =>
          String(a?.symbol ?? "").localeCompare(String(b?.symbol ?? ""), undefined, { sensitivity: "base" })
        );
      if (selected.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching stocks found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const csv = buildStockArkEtfCsvExportText(selected);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`ark-etf-export-${datePart}.csv`, csv);
      onError("");
    } catch (err) {
      onError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Delete ARK ETF",
        message: "Please select at least one record to delete",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete ARK ETF",
      mode: "confirm",
      message: `Remove ${selectedVisibleIds.size} symbol(s) from the ARK ETF list? This does not delete Holdings records.`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(`${serverUrl}/deleteStockArkBuy`, {
              method: "POST",
              body: JSON.stringify(stockRequestBody(stockSession, { _id })),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        await fetchList();
        setDeleting(false);
      },
    });
  }

  const handleSymbolOpen = useCallback(
    (row) => {
      if (!row?.stock_id || !onOpenStock) return;
      onOpenStock({ ...row, _id: row.stock_id });
    },
    [onOpenStock]
  );

  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <TableViewport>
      <CrudListToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search symbol, name, sector, industry, rating, trade date…"
        onClearSearch={() => setSearchTerm("")}
        showCsvImport={false}
        showNew={false}
        extraActions={
          <>
            {holdingsUrl ? (
              <a
                href={holdingsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-md btn-secondary"
              >
                Open ARK holdings
              </a>
            ) : null}
            {tradesUrl ? (
              <a
                href={tradesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-md btn-secondary"
              >
                Open ARK trades
              </a>
            ) : null}
            <button
              type="button"
              className="btn btn-md btn-primary-outline"
              disabled={selectedVisibleCount === 0 || exporting}
              onClick={() => exportSelectedCsv()}
            >
              {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
            </button>
            <DataRefreshControl
              source="Cathie's Ark"
              lastRefreshedAt={fetchedAt || null}
              refreshing={refreshing}
              disabled={busy}
              onRefresh={() => refreshFromArk()}
              title="Refresh Buy tickers from Cathie's Ark combined trades"
            />
          </>
        }
        onDeleteSelected={deleteSelected}
        deleteDisabled={selectedVisibleCount === 0 || deleting}
        deleteCountSuffix={selectedCountSuffix}
        showViewToggle={false}
      />

      <div className="panel" style={{ marginBottom: 8 }}>
        <p className="panel-hint" style={{ margin: 0 }}>
          Full ARK combined holdings from Cathie&apos;s Ark with <strong>ARK Market Value</strong> and{" "}
          <strong>Weight %</strong>. <strong>Trade Date</strong> shows the latest Buy from ARK trades when
          applicable. Other columns match your Holdings when that symbol is saved there. Delete removes
          symbols from this list only.
        </p>
      </div>

      {currentHoldingFilter ? (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span className="panel-hint-inline">
              Drilldown filter active: Current holding (equity &gt; 0) • {earningsFilteredRows.length} stock
              {earningsFilteredRows.length === 1 ? "" : "s"}
            </span>
            {onCurrentHoldingClick ? (
              <button type="button" className="btn btn-sm btn-secondary" onClick={onCurrentHoldingClick}>
                Clear drilldown
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {earningsUpcomingFilter ? (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span className="panel-hint-inline">
              Drilldown filter active: Upcoming earnings (30 days) • {earningsFilteredRows.length} stock
              {earningsFilteredRows.length === 1 ? "" : "s"}
            </span>
            {onUpcomingEarningsClick ? (
              <button type="button" className="btn btn-sm btn-secondary" onClick={onUpcomingEarningsClick}>
                Clear drilldown
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasLoadedList && !loadingList ? (
        <StockSummaryStatCards
          stockCount={listSummary.stockCount}
          showTotalInvestment={false}
          currentHoldingCount={currentHoldingCount}
          currentHoldingActive={currentHoldingFilter}
          onCurrentHoldingClick={onCurrentHoldingClick}
          upcomingEarningsCount={upcomingEarningsCount}
          upcomingEarningsActive={earningsUpcomingFilter}
          onUpcomingEarningsClick={onUpcomingEarningsClick}
          recentBuyCount={recentBuyCount}
          recentBuyActive={recentBuyFilter}
          onRecentBuyClick={onRecentBuyClick}
        />
      ) : null}

      {loadingList ? <p className="panel-hint">Loading ARK ETF list…</p> : null}

      {showEmptyState ? (
        <p className="panel-hint">
          No ARK ETF data saved yet. Use the refresh control above to load complete holdings and recent
          Buy trades from Cathie&apos;s Ark.
        </p>
      ) : null}

      {showTable ? (
        <>
          <ListPaginationBar {...paginationBarProps} />
          <div className="table-scroll">
            <div className={`table-wrapper table-wrapper-stock-mgmt table-wrapper--sticky-header ${freezeClass}`.trim()}>
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                      />
                    </th>
                    {displayColumns.map((col) => (
                      <th key={col.id} className="sortable" onClick={() => handleSort(col.id)}>
                        <span className="table-th-label">{col.label}</span>
                      </th>
                    ))}
                    <TableActionColumnHeader
                      onCustomizeColumns={onOpenColumnCustomizer || undefined}
                      customizeDisabled={columnPrefsSaving}
                    />
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((row) => (
                    <tr key={row._id || row.symbol}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row._id)}
                          onChange={(e) => handleSelectOne(row._id, e.target.checked)}
                        />
                      </td>
                      {displayColumns.map((col) => (
                        <td key={col.id}>
                          <StockHoldingsTableCell
                            colId={col.id}
                            row={row}
                            onSymbolClick={handleSymbolOpen}
                          />
                        </td>
                      ))}
                      <td className="text-center" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </TableViewport>
  );
}

export default StockArkBuy;
